import { Component, NgZone } from '@angular/core';
import { DataService } from '../../../../core/services/data.service';
import { AuthService } from '../../../../core/services/auth.service';
import { FormBuilder, Validators } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import i18next from 'i18next';
import { NativeIntegrationService } from '../../../../core/services/native-integration.service';
import { BridgeMode } from '@hg-com/xwc-js-bridge/dist/native-app.service';
import { environment } from '../../../../environments/environment';
import { LanguageService } from '../../../../core/services/language.service';
import { AnalyticsService } from 'src/core/services/analytics.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent {
  passwortVisible = false;
  passwordLost = false;
  pinLost = false;
  username = localStorage.getItem('username') ?? ' ';
  password!: string;
  authSub?: Subscription;
  biometric = false;
  online = true;
  currentApplicationVersion = environment.appVersion;
  loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });
  supportUrl = 'https://www.vb-serviceportal.com/password-reset?userLocale=';
  supportUrlLangRelated = this.supportUrl;
  lang = this.languageService.i18NextService.language;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private dataService: DataService,
    public nis: NativeIntegrationService,
    private authService: AuthService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private languageService: LanguageService,
    private ngZone: NgZone,
    // Hier damit es initialisiert wird
    // Eigentlich nicht nötig da durch authService geladen
    // ich will das aber explicit haben
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    // Support URL für die verschiedenen Sprachen
    this.supportUrlLangRelated = this.supportUrl + this.lang;
    // Subscriber für Sprachwechsel
    this.languageService.i18NextService.events.initialized.subscribe((e) => {
      if (e) {
        console.log('[DATA SERVICE]: Language subscription');
        this.lang = this.languageService.i18NextService.language;
        // Aktualisiert Support Link bei Sprachwechsel
        this.supportUrlLangRelated = this.supportUrl + this.lang;
        console.log(this.supportUrl);
      }
    });

    const errorSub = this.authService.error.subscribe((err) => {
      if (err) {
        console.log('[LOGIN]: Authentication Error: ' + err);
        this.toastr.error(
          i18next.t('login.login-error-txt'),
          i18next.t('login.login-error-title')
        );
        this.authService.error.next(false);
      }
    });
    this.subscriptions.add(errorSub);

    combineLatest([
      this.nis.isNative$,
      this.nis.isReady$,
      this.authService.hasPin$,
      this.authService.username$,
    ]).subscribe(([isNative, isReady, hasPin, username]) => {
      if (isNative && isReady && hasPin && username) {
        this.ngZone.run(() => (this.biometric = true));
      }
    });

    // Fehlerausgabe Login
    this.nis.spinTillReady().then(() => {
      // Checkt, ob man in nativer Umgebung ist
      if (this.nis.isNative$.getValue()) {
        // Prüft, ob ein Pin bereits vergeben wurde
        // Prüft, ob man eine Internetverbindung hat
        this.nis.checkConnection();
        // Verbindungsprüfung, wenn sich was am Netzwerk ändert
        const networkSub = this.nis.network.networkStateChange.subscribe(
          (state) => {
            console.log('[LOGIN]: Network state change detected');
            this.nis.checkConnection();
          }
        );
        this.subscriptions.add(networkSub);
        // Online Subscriber, blendet Offline Indicator ein wenn keine Verbindung da isr
        const onlineSub = this.nis.online$.subscribe((online) => {
          if (online) {
            this.online = true;
          } else {
            this.online = false;
          }
        });
        this.subscriptions.add(onlineSub);
      }
    });
  }

  // Setzt den Benutzernamen
  setUsername(event: any): void {
    this.username = event.value;
    if (this.username !== null) {
      localStorage.setItem('username', this.username);
    }
  }

  // Setzt das Passwort
  setPassword(event: any): void {
    this.password = event.value;
  }

  // Startet Authentifizierungsprozess
  handleLogin(): void {
    if (
      this.username &&
      this.password &&
      !this.authService.contextNative$.value
    ) {
      console.log('[LOGIN]: Authenticate Online');
      this.authService.authenticate(this.username, this.password);
    } else {
      if (this.username) {
        console.log('[LOGIN]: Authenticate Native');
        this.authService.authenticateNative(this.username, this.password);
      }
    }
  }

  // Collections und Produktdatenabruf
  getData(): void {
    console.log('[LOGIN]: Get data');

    const translationsSub = this.dataService.translationsLoaded$.subscribe(
      (loaded) => {
        console.log(
          '[LOGIN]: Translations loaded check - Subscription: ' + loaded
        );
      }
    );
    this.subscriptions.add(translationsSub);
  }

  // Öffnet Browser des nativen Devices
  openBrowser(): void {
    this.nis.basics.openExternalBrowser$(this.supportUrlLangRelated);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  togglePasswordVisible() {
    this.passwortVisible = !this.passwortVisible;
  }

  passwortInputType() {
    return this.passwortVisible ? 'text' : 'password';
  }
}
